import cx from "classnames"
import { observer } from "mobx-react"
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef } from "react"
import { useComputers } from "../computers/context"
import { projectColorAssignments } from "../projectAppearance"
import { useCodeStore } from "../store/context"
import type { Repo } from "../types"
import { loadWorkspaceSidebarState, orderWorkspaces } from "./sidebar/workspaceSidebarState"
import { ProjectIcon } from "./sidebar/ProjectIcon"

function isTextEntryTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false
    if (target.closest("input, textarea, select, [contenteditable='true'], [contenteditable='plaintext-only']")) return true
    return false
}

export const TaskCreateWorkspacePicker = observer(({ workspaceId, onSelect }: { workspaceId: string; onSelect: (workspaceId: string) => void }) => {
    const codeStore = useCodeStore()
    const computers = useComputers()
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
    const sidebarOrder = loadWorkspaceSidebarState(computers.scope)?.workspaceOrder ?? []
    const orderedRepos = orderWorkspaces(codeStore.repos.repos, sidebarOrder)
    const activeRepos = orderedRepos.filter((repo) => !repo.archived || repo.id === workspaceId)
    const colorByWorkspaceId = projectColorAssignments(activeRepos)

    useEffect(() => {
        const handleNumberShortcut = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.isComposing || event.altKey || event.shiftKey) return
            if (!/^[1-9]$/.test(event.key)) return

            const hasCommandModifier = event.metaKey || event.ctrlKey
            if (!hasCommandModifier && isTextEntryTarget(event.target)) return

            const repo = activeRepos[Number(event.key) - 1]
            if (!repo || repo.id === workspaceId) return

            event.preventDefault()
            onSelect(repo.id)
        }

        window.addEventListener("keydown", handleNumberShortcut)
        return () => window.removeEventListener("keydown", handleNumberShortcut)
    }, [activeRepos, onSelect, workspaceId])

    const handleCellKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | null = null
        if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (index - 1 + activeRepos.length) % activeRepos.length
        if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (index + 1) % activeRepos.length
        if (event.key === "Home") nextIndex = 0
        if (event.key === "End") nextIndex = activeRepos.length - 1
        if (nextIndex === null) return

        event.preventDefault()
        const nextRepo = activeRepos[nextIndex]
        if (!nextRepo) return
        buttonRefs.current[nextIndex]?.focus()
        if (nextRepo.id !== workspaceId) onSelect(nextRepo.id)
    }

    return (
        <div className="border-b border-border bg-base-200/30 px-4 py-3 max-[900px]:px-3">
            <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">Project</span>
                {activeRepos.length > 1 && <span className="text-[11px] text-muted">⌘1–9 to switch</span>}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-0.5" role="group" aria-label="Choose a project for the new task">
                {activeRepos.map((repo: Repo, index: number) => {
                    const selected = repo.id === workspaceId
                    const shortcutNumber = index + 1
                    const keyboardShortcut = shortcutNumber <= 9 ? `Meta+${shortcutNumber} Control+${shortcutNumber}` : undefined
                    return (
                        <button
                            key={repo.id}
                            ref={(element) => {
                                buttonRefs.current[index] = element
                            }}
                            type="button"
                            className={cx(
                                "btn flex min-h-10 min-w-[9rem] max-w-[16rem] flex-1 items-center gap-2 border px-3 py-2 text-left text-sm transition-colors max-[900px]:min-h-11 max-[900px]:min-w-[10rem]",
                                selected
                                    ? "border-primary bg-primary/10 text-base-content"
                                    : "border-border bg-base-100 text-muted hover:border-base-content/30 hover:bg-base-200 hover:text-base-content"
                            )}
                            aria-pressed={selected}
                            aria-keyshortcuts={keyboardShortcut}
                            onClick={() => {
                                if (!selected) onSelect(repo.id)
                            }}
                            onKeyDown={(event) => handleCellKeyDown(event, index)}
                            title={shortcutNumber <= 9 ? `${repo.name} (⌘${shortcutNumber})` : repo.name}
                            data-workspace-picker-id={repo.id}
                        >
                            <span
                                className={cx(
                                    "flex h-5 min-w-5 shrink-0 items-center justify-center border px-1 text-[10px] font-semibold tabular-nums",
                                    selected ? "border-primary/40 text-primary" : "border-border text-muted"
                                )}
                                aria-hidden="true"
                            >
                                {shortcutNumber}
                            </span>
                            <ProjectIcon
                                projectId={repo.id}
                                projectName={repo.name}
                                color={colorByWorkspaceId.get(repo.id)}
                                glyph={repo.glyph}
                                projectChars={repo.projectChars}
                                projectEmoji={repo.projectEmoji}
                                variant="glyph"
                                nativeTitle={false}
                            />
                            <span className="min-w-0 flex-1 truncate font-medium">{repo.name}</span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
})
